import React from "react";
import { Text, TextVariant } from "../text/text";
import styles from "./tag.module.css";
import { withSafeRender } from "../safe-render";

/**
 * Tag — a short piece of promotional copy on a coloured pill.
 *
 * Server Component: no state, no handlers, no client JavaScript.
 *
 * NOT A CHIP. A chip is a control the user picks, so it is focusable and
 * announces a pressed state. A Tag is read, never operated — hence a plain
 * <span> with no role and no tabindex. Adding either would put a dead stop in
 * the tab order announcing a control that does nothing.
 *
 * Source: FUSION ECLIPSE "UI Kit", Tag-onDark 10188:17218.
 */
/* The single source of truth for both axes: the membership guards, the spec's
   stylesheet sweep and the stories all derive from these two arrays, so a new
   variant cannot be added in one place and silently skipped in another. */
export const TAG_COLORS = ["yellow", "turquoise", "blur", "blue", "magenta"] as const;
export const TAG_SIZES = ["responsive", "desktop", "tablet", "mobile"] as const;

export type TagColor = (typeof TAG_COLORS)[number];
export type TagSize = (typeof TAG_SIZES)[number];

export interface TagProps {
  color?: TagColor;
  /**
   * The Figma variant this Tag was drawn as. `mobile` renders the set's 12px
   * `Label Small medium` step; the other three render Body 3 at 14px.
   */
  size?: TagSize;
  className?: string;
  children: React.ReactNode;
}

function membership<T extends string>(values: readonly T[]): Record<T, true> {
  const set = {} as Record<T, true>;
  for (const value of values) {
    set[value] = true;
  }
  return set;
}

const COLORS = membership(TAG_COLORS);
const SIZES = membership(TAG_SIZES);

/* Mobile binds `Mobile/UI/Label Small medium` (12px), the other three bind
   `Body 3 medium` (14px) — Tag-onDark 10188:17218. Design conflicts → Tag. */
const VARIANT_FOR_SIZE: Record<TagSize, TextVariant> = {
  responsive: "body-3",
  desktop: "body-3",
  tablet: "body-3",
  mobile: "label"
};

/** The value if the system publishes it, the default if it does not. */
function published<T extends string>(
  set: Record<T, true>,
  value: string,
  fallback: T
): T {
  return Object.prototype.hasOwnProperty.call(set, value) ? (value as T) : fallback;
}

function TagImpl({
  color = "yellow",
  size = "responsive",
  className,
  children
}: TagProps) {
  const resolvedColor = published(COLORS, color, "yellow");
  const resolvedSize = published(SIZES, size, "responsive");

  const classes = [styles.tag, styles[resolvedColor], className]
    .filter(Boolean)
    .join(" ");

  return (
    <Text
      as="span"
      className={classes}
      variant={VARIANT_FOR_SIZE[resolvedSize]}
      weight="medium"
    >
      {children}
    </Text>
  );
}
export const Tag = withSafeRender(TagImpl, "Tag");

export default Tag;
